<template>
  <div>
    <v-layout row align-center>
      <v-autocomplete
        label="Name"
        :placeholder="loading ? 'loading...' : 'Start typing...'"
        :items="allUsers"
        :search-input.sync="search"
        :item-text="getUserDisplayName"
        :filter="filterAllUsers"
        :loading="loading"
        :value="value"
        @input="$emit('input', $event || null)"
        return-object
        clearable
        :rules="[rules.required]"
      >
        <template v-slot:item="{ item }">
          <v-list-tile-content style="padding: 4px 0;">
            <v-list-tile-title style="font-size: 14px; font-weight: 600; margin-bottom: 4px;">{{ item.userType === 'external' ? item.fullName : (item.name || item.userName) }}</v-list-tile-title>
            <v-list-tile-sub-title v-if="item.userType !== 'external' && item.name" style="font-size: 12px; margin-bottom: 2px;">{{ item.userName }}</v-list-tile-sub-title>
            <v-list-tile-sub-title v-if="item.email && (item.userType === 'external' || item.email !== item.userName)" style="font-size: 12px; margin-bottom: 2px;">{{ item.email }}</v-list-tile-sub-title>
            <v-list-tile-sub-title v-if="item.phoneNumber" style="font-size: 12px;">{{ item.phoneNumber }}</v-list-tile-sub-title>
          </v-list-tile-content>
        </template>
      </v-autocomplete>
    </v-layout>
    <span
      v-if="value && (!value.email && !value.phoneNumber)"
      class="caption red--text text-xs-center"
      >Contact details are required to receive login information.</span
    >

    <div
      v-if="value"
      style="display: flex; align-items: center; justify-content: space-between;"
    >
      <div>
        <div v-if="value.name" style="font-size: 12px; color: #666;">
          Name: <b>{{ value.name || "-" }}</b>
        </div>
        <div style="font-size: 12px; color: #666;">
          Email: <b>{{ value.email || "-" }}</b>
        </div>
        <div style="font-size: 12px; color: #666;">
          Phone Number: <b>{{ value.phoneNumber || "-" }}</b>
        </div>
      </div>
      <v-btn
        flat
        small
        color="grey"
        title="Edit user"
        @click="openEditUserInfoDialog(value)"
      >
        <v-icon small>mdi-pencil</v-icon> &nbsp; Edit User Info
      </v-btn>
    </div>

    <v-dialog
      content-class="dialog-top"
      v-if="editUserInfo"
      v-model="editUserInfoDialog"
      max-width="500"
      persistent
    >
      <v-card>
        <v-card-title class="headline green lighten-3"
          >Edit User Info for
          {{
            editUserInfo.user.userName || editUserInfo.user.fullName
          }}</v-card-title
        >
        <v-form v-model="editUserFormValid">
          <v-card-text class="pt-3">
            <v-text-field
              v-model="editUserInfo.user.email"
              label="Email"
              type="email"
              :rules="[rules.email]"
            ></v-text-field>
            <v-text-field
              v-model="editUserInfo.user.phoneNumber"
              label="Phone Number"
              :rules="[rules.phone]"
            ></v-text-field>
          </v-card-text>
        </v-form>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            flat
            small
            outline
            color="default"
            @click="
              editUserInfoDialog = false;
              editUserInfo = null;
            "
            >Cancel</v-btn
          >
          <v-btn
            flat
            small
            outline
            color="success"
            @click="updateUser()"
            :loading="updateUserProcessing"
            :disabled="!editUserFormValid"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "ConferenceRoomAllUsers",
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    externalUsers: {
      type: Array,
      default: [],
    },
    internalUsers: {
      type: Array,
      default: [],
    },
    value: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      search: "",
      editUserInfoDialog: false,
      editUserInfo: null,
      editUserFormValid: true,
      updateUserProcessing: false,
      rules: {
        required: (value) => !!value || "Required.",
        email: (v) =>
          !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "Invalid email address",
        phone: (v) =>
          !v ||
          /^\d{10,11}$/.test(v) ||
          "Phone number must be a valid US number, 10 or 11 digits without dashes or parentheses",
      },
    };
  },
  computed: {
    allUsers() {
      return [...this.internalUsers, ...this.externalUsers];
    },
  },
  methods: {
    getUserDisplayName(item) {
      return item.userType === "external" ? item.fullName : (item.name || item.userName);
    },
    filterAllUsers(item, queryText) {
      if (item.userType === "external") {
        return this.filterExternalUsers(item, queryText);
      }
      return this.filterInternalUsers(item, queryText);
    },
    filterInternalUsers(item, queryText) {
      const q = queryText.toLowerCase();
      return (
        (item.userName || "").toLowerCase().includes(q) ||
        (item.phoneNumber || "").trim().includes(q) ||
        (item.email || "").toLowerCase().includes(q) ||
        (item.name || "").toLowerCase().includes(q)
      );
    },
    filterExternalUsers(item, queryText) {
      const q = queryText.toLowerCase();
      return (
        (item.fullName || "").toLowerCase().includes(q) ||
        (item.phoneNumber || "").trim().includes(q) ||
        (item.email || "").toLowerCase().includes(q)
      );
    },

    openEditUserInfoDialog(user) {
      this.editUserInfo = {
        user: { ...user },
        originalUser: user,
      };
      this.editUserInfoDialog = true;
    },
    updateUser() {
      if (this.editUserInfo.user.userType === "internal") {
        this.updateInternalUser();
      } else {
        this.updateExternalUser();
      }
    },
    async updateInternalUser() {
      this.updateUserProcessing = true;
      try {
        const roles = await this.$http.get(
          "api/login/GetUserRoles?userName=" + this.editUserInfo.user.userName
        );
        const u = this.editUserInfo.user;
        await this.$http.post("api/login/UpdateUser", {
          username: u.userName,
          branch: u.branch || "",
          roles: roles,
          phoneNumber: u.phoneNumber,
          email: u.email,
          utaPhone: u.utaPhone,
          disabled: u.disabled || false,
        });
        this.editUserInfo.originalUser.email = this.editUserInfo.user.email;
        this.editUserInfo.originalUser.phoneNumber = this.editUserInfo.user.phoneNumber;
        this.editUserInfoDialog = false;
        this.editUserInfo = null;
        this.$emit("update:internalUsers");
        this.$notify({
          text: "User info updated successfully",
          group: "notification",
          type: "success",
        });
      } catch (error) {
        this.$notify({
          text: "Error updating user info",
          group: "notification",
          type: "error",
        });
      }
      this.updateUserProcessing = false;
    },
    async updateExternalUser() {
      this.updateUserProcessing = true;
      try {
        await this.$http.post(
          "api/ConferenceRoomUsers/Update",
          this.editUserInfo.user
        );
        this.editUserInfo.originalUser.email = this.editUserInfo.user.email;
        this.editUserInfo.originalUser.phoneNumber = this.editUserInfo.user.phoneNumber;
        this.editUserInfoDialog = false;
        this.editUserInfo = null;
        this.$emit("update:externalUsers");
        this.$notify({
          text: "User info updated successfully",
          group: "notification",
          type: "success",
        });
      } catch (error) {
        this.$notify({
          text: "Error updating user info",
          group: "notification",
          type: "error",
        });
      }
      this.updateUserProcessing = false;
    },
  },
};
</script>

<style>
.v-autocomplete__content .v-list__tile {
  height: auto !important;
  min-height: 48px;
  padding: 8px 16px;
}
</style>
